import { useEffect, useMemo, useState } from 'react';
import { Check, Loader2, X as XIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { CLEAR_ON_CHANGE, DynamicSelect, isFieldVisible } from './DynamicForm';

const EDITABLE_TYPES = new Set(['text', 'email', 'phone', 'number', 'date', 'select', 'textarea', 'checkbox']);

function formatDateInput(v) {
    if (!v) return '';
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
    return d.toISOString().slice(0, 10);
}

function formatValueForInput(field, raw) {
    if (raw == null || raw === '') return field.type === 'checkbox' ? false : '';
    if (field.type === 'checkbox') return !!raw;
    if (field.type === 'number') return raw;
    if (field.type === 'date') return formatDateInput(raw);
    return String(raw);
}

function filterVisibleFields(fields, formData) {
    return (fields || []).filter(f =>
        f.key
        && EDITABLE_TYPES.has(f.type)
        && f.type !== 'header'
        && !f.isArchived
        && isFieldVisible(f, formData)
    );
}

/** Collect editable schema fields grouped by step (visibility applied at render). */
export function getEditableSchemaFields(schema, formData = {}) {
    const groups = [];
    for (const step of schema?.steps || []) {
        const fields = filterVisibleFields(step.fields, formData);
        if (fields.length) groups.push({ step, fields });
    }
    return groups;
}

/** Pre-fill form state from submission.data using actual schema field keys. */
export function buildFormFromSubmission(schema, data = {}) {
    const form = {};
    for (const step of schema?.steps || []) {
        for (const field of step.fields || []) {
            if (!field.key || !EDITABLE_TYPES.has(field.type) || field.isArchived) continue;
            form[field.key] = formatValueForInput(field, data[field.key]);
        }
    }
    return form;
}

function diffPatch(originalData, form, schema) {
    const patch = {};
    const seen = new Set();
    for (const step of schema?.steps || []) {
        for (const field of filterVisibleFields(step.fields, form)) {
            if (seen.has(field.key)) continue;
            seen.add(field.key);
            const k = field.key;
            const v = form[k];
            const orig = originalData[k];
            const normOrig = orig === undefined || orig === null ? '' : orig;
            const normNext = v === undefined || v === null ? '' : v;
            if (String(normOrig) !== String(normNext)) patch[k] = v;
        }
    }
    return patch;
}

function SchemaField({ field, value, onChange, formData }) {
    const label = field.label || field.key;
    const id = `edit-${field.key}`;
    const req = field.required ? ' *' : '';

    if (field.type === 'textarea') {
        return (
            <div className="sm:col-span-2">
                <label className="ag-label" htmlFor={id}>{label}{req}</label>
                <textarea
                    id={id}
                    className="ag-input min-h-[72px]"
                    value={value ?? ''}
                    onChange={e => onChange(e.target.value)}
                />
            </div>
        );
    }

    if (field.type === 'select') {
        return (
            <div>
                <label className="ag-label" htmlFor={id}>{label}{req}</label>
                {field.optionsSource ? (
                    <DynamicSelect field={field} value={value} onChange={onChange} data={formData} />
                ) : (
                    <select id={id} className="ag-input" value={value ?? ''} onChange={e => onChange(e.target.value)}>
                        <option value="">{field.placeholder || 'Select…'}</option>
                        {(field.options || []).map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label ?? opt.value}</option>
                        ))}
                    </select>
                )}
            </div>
        );
    }

    if (field.type === 'checkbox') {
        return (
            <div className="flex items-center gap-2 pt-6">
                <input
                    id={id}
                    type="checkbox"
                    checked={!!value}
                    onChange={e => onChange(e.target.checked)}
                />
                <label htmlFor={id} className="text-sm font-medium text-ink-800">{label}</label>
            </div>
        );
    }

    const inputType = field.type === 'email' ? 'email'
        : field.type === 'number' ? 'number'
        : field.type === 'date' ? 'date'
        : field.type === 'phone' ? 'tel'
        : 'text';

    return (
        <div>
            <label className="ag-label" htmlFor={id}>{label}{req}</label>
            <input
                id={id}
                className="ag-input"
                type={inputType}
                value={value ?? ''}
                placeholder={field.placeholder || ''}
                onChange={e => onChange(
                    field.type === 'number'
                        ? (e.target.value === '' ? '' : Number(e.target.value))
                        : e.target.value
                )}
            />
        </div>
    );
}

/**
 * Edit modal — schema fields pre-filled; same visibleIf rules as client registration.
 */
export default function ClientChangeRequestModal({ submission, onClose }) {
    const data = submission?.data || {};
    const schema = submission?.schema;

    const [f, setF] = useState(() => buildFormFromSubmission(schema, data));
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);

    const groups = useMemo(() => getEditableSchemaFields(schema, f), [schema, f]);

    useEffect(() => {
        setF(buildFormFromSubmission(submission?.schema, submission?.data || {}));
        setReason('');
    }, [submission?._id]);

    const setField = (key, val) => {
        setF(prev => {
            const next = { ...prev, [key]: val };
            for (const dep of CLEAR_ON_CHANGE[key] || []) delete next[dep];
            return next;
        });
    };

    const submit = async (e) => {
        e.preventDefault();
        const patch = diffPatch(data, f, schema);
        if (!Object.keys(patch).length) {
            toast.error('Change at least one field before submitting');
            return;
        }
        setBusy(true);
        try {
            const { data: res } = await api.post(`/forms/submissions/${submission._id}/change-requests/update`, {
                patch,
                reason,
            });
            toast.success('Update request sent — admin will review');
            onClose(res.submission);
        } catch {
            /* interceptor */
        } finally {
            setBusy(false);
        }
    };

    if (!schema?.steps?.length) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink-900/50 backdrop-blur-sm">
                <div className="rounded-2xl bg-white p-6 max-w-md shadow-xl ring-1 ring-ink-200">
                    <p className="text-sm text-ink-600">No editable fields found for this client form.</p>
                    <button type="button" className="ag-btn-primary mt-4" onClick={() => onClose(null)}>Close</button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink-900/50 backdrop-blur-sm" role="dialog" aria-modal="true">
            <div className="w-full max-w-3xl rounded-2xl bg-white shadow-xl ring-1 ring-ink-200 max-h-[92vh] flex flex-col">
                <div className="px-5 py-4 border-b border-ink-100 flex items-center justify-between shrink-0">
                    <div>
                        <h3 className="font-extrabold">Edit client details</h3>
                        <p className="text-[11px] text-ink-500 mt-0.5">
                            Same fields as registration — only what applies to this franchise type is shown.
                        </p>
                    </div>
                    <button type="button" className="text-ink-500 hover:text-brand-red" onClick={() => onClose(null)} aria-label="Close">
                        <XIcon className="h-4 w-4" />
                    </button>
                </div>

                <form onSubmit={submit} className="p-5 space-y-5 overflow-y-auto">
                    {groups.length === 0 ? (
                        <p className="text-sm text-ink-500 text-center py-6">No editable fields for the current selections.</p>
                    ) : groups.map(({ step, fields }) => (
                        <div key={step.title}>
                            <div className="text-[10px] uppercase tracking-wider text-ink-500 font-semibold mb-2">
                                {step.title}
                            </div>
                            {step.description && (
                                <p className="text-[11px] text-ink-500 mb-2">{step.description}</p>
                            )}
                            <div className="rounded-xl ring-1 ring-ink-100 p-3 bg-ink-50/40 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {fields.map(field => (
                                    <SchemaField
                                        key={field.key}
                                        field={field}
                                        value={f[field.key]}
                                        formData={f}
                                        onChange={v => setField(field.key, v)}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}

                    <div>
                        <label className="ag-label">Why are you requesting these changes? (optional)</label>
                        <textarea
                            className="ag-input min-h-[80px]"
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            placeholder="e.g. client corrected phone number on call"
                        />
                    </div>

                    <div className="flex justify-end gap-2 pt-2 border-t border-ink-100">
                        <button type="button" className="ag-btn-ghost" onClick={() => onClose(null)}>Cancel</button>
                        <button type="submit" className="ag-btn-primary" disabled={busy}>
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                            {busy ? 'Sending…' : 'Send for approval'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
